import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../../Redux/hooks';
import {
  selectAbsencesByStudent,
  selectActiveTaughtStudents,
  selectNextLesson,
  selectSlotsByStudent,
  selectTodayAppointments,
  selectTodayLessons,
  selectTotals
} from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LessonModel } from '../../../Models/LessonModel';
import { LessonStatus, type Language } from '../../../Models/enums';
import { formatDate } from '../../../Utils/DateUtil';
import { routes } from '../../../Utils/routes';
import { amountValue } from '../../../Utils/MoneyUtil';
import { isCollected, isOwed, localDateString } from '../../../Utils/LessonStatusUtil';
import { plannedLessonsFor, type PlannedLesson } from '../../../Utils/PlannedLessonUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { QuickAddLesson } from '../Lessons/QuickAddLesson';
import { DebtReminderAction } from '../Students/DebtReminderAction/DebtReminderAction';
import { StarterChecklist } from './StarterChecklist';
import './Dashboard.css';
import { useDisplayName } from '../../../Utils/useDisplayName';

interface BarDatum { label: string; value: number }

export function Dashboard() {
  const displayName = useDisplayName();
  const { tr, language } = useTranslation();
  const totals = useAppSelector(selectTotals);
  const today = useAppSelector(selectTodayLessons);
  const todayAppointments = useAppSelector(selectTodayAppointments);
  const next = useAppSelector(selectNextLesson);
  const slotsByStudent = useAppSelector(selectSlotsByStudent);
  const students = useAppSelector(selectActiveTaughtStudents);
  const allStudents = useAppSelector(store => store.students.items);
  const lessons = useAppSelector(store => store.lessons.items);
  const state = useAppSelector(store => store.lessons.state);
  const error = useAppSelector(store => store.lessons.error);
  const absencesByStudent = useAppSelector(selectAbsencesByStudent);
  const [adding, setAdding] = useState(false);
  const [plannedAdd, setPlannedAdd] = useState<PlannedLesson | null>(null);

  /**
   * Today is what the tutor is about to do, and most of it has no lesson row
   * yet: a student's regular hour becomes a row when the lesson is booked,
   * given or paid for. So the strip lists the booked lessons and the
   * recurring hours still expected, in the order they will happen. A
   * cancelled lesson is neither: it is not listed, but it still books its
   * hour so the recurring placeholder does not come back in its place. The
   * date is local on purpose; toISOString is UTC and turns into tomorrow at
   * 21:00 Israel time.
   */
  const todayKey = localDateString();
  const todayPlanned = useMemo(
    () => plannedLessonsFor(todayKey, slotsByStudent, absencesByStudent, today),
    [todayKey, slotsByStudent, absencesByStudent, today]
  );
  const todayEntries = useMemo(() => {
    const entries: Array<{ time: string; lesson?: LessonModel; planned?: PlannedLesson }> = [
      ...todayAppointments.map(lesson => ({ time: lesson.time ?? '', lesson })),
      ...todayPlanned.map(planned => ({ time: planned.time, planned }))
    ];
    return entries.sort((a, b) => a.time.localeCompare(b.time));
  }, [todayAppointments, todayPlanned]);

  const collectedLessons = useMemo(() => lessons.filter(isCollected), [lessons]);
  // The headline figure is this year's income, not everything the account has
  // ever taken in: `totals.collected` (below) stays lifetime, because the
  // average-per-lesson figure it feeds is a lifetime average on purpose.
  const currentYear = new Date().getFullYear();
  const yearCollected = useMemo(
    () => collectedLessons
      .filter(lesson => lesson.date.startsWith(String(currentYear)))
      .reduce((sum, lesson) => sum + amountValue(lesson.amount), 0),
    [collectedLessons, currentYear]
  );
  const owedLessons = useMemo(() => lessons.filter(lesson =>
    isOwed(lesson, slotsByStudent.get(lesson.student) ?? [])
  ), [lessons, slotsByStudent]);
  const debtGroups = useMemo(() => allStudents
    .map(student => {
      const studentLessons = owedLessons.filter(lesson => lesson.student === student.name);
      return {
        student,
        lessons: studentLessons,
        total: studentLessons.reduce((sum, lesson) => sum + amountValue(lesson.amount), 0)
      };
    })
    .filter(group => group.lessons.length > 0)
    .sort((a, b) => b.total - a.total), [allStudents, owedLessons]);
  const average = collectedLessons.length ? Math.round(totals.collected / collectedLessons.length) : 0;
  const charts = useMemo(() => buildCharts(lessons, language), [lessons, language]);

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} />;
  if (students.length === 0) {
    return <EmptyState title={tr('noStudents')} hint={tr('noStudentsHint')} action={<Link className="primary-button" to={routes.students}>{tr('addStudent')}</Link>} />;
  }

  return (
    <section className="dashboard">
      {/* Above everything, and only until its three steps are done. A new
          tutor with an empty app needs a next action, not a summary of
          nothing. */}
      <StarterChecklist />

      <section className="today-strip" aria-labelledby="today-heading">
        <header className="today-strip-header">
          <span className="dashboard-eyebrow" id="today-heading">{tr('todayLessons')}<span className="visually-hidden">{tr('todayTitle')}</span></span>
          <strong><bdi>{formatDate(todayKey, language)}</bdi></strong>
        </header>

        {todayEntries.length === 0
          ? <p className="dashboard-quiet">{tr('todayNone')}</p>
          : (
            <ul className="today-lesson-list">
              {todayEntries.map(entry => entry.lesson
                ? <TodayLesson key={`lesson-${entry.lesson.id}`} lesson={entry.lesson} />
                : entry.planned && (
                  <TodayPlanned
                    key={`planned-${entry.planned.student}-${entry.planned.time}`}
                    item={entry.planned}
                    price={allStudents.find(student => student.name === entry.planned?.student)?.price ?? null}
                    onAdd={setPlannedAdd}
                  />
                ))}
            </ul>
          )}

        <div className="today-quick-actions">
          <button type="button" className="dashboard-primary-action" onClick={() => setAdding(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            {tr('addLesson')}
          </button>
          <Link className="dashboard-secondary-action" to={routes.students}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2" /><circle cx="17" cy="9" r="2.6" /><path d="M3 19c.6-3.2 3.1-5 6-5s5.4 1.8 6 5M14.5 19c.4-2.4 2.1-3.8 4.3-3.8s2.7.7 3.2 1.8" /></svg>
            {tr('navStudents')}
          </Link>
        </div>
      </section>

      {owedLessons.length > 0 && (
        <section className="dashboard-debt-panel" aria-labelledby="dashboard-debt-title">
          <header className="dashboard-debt-header">
            <span className="overdue-icon" aria-hidden="true">!</span>
            <span>
              <strong id="dashboard-debt-title">{tr('debtStudentsTitle')}</strong>
              <small>{tr('openDebtAlert', owedLessons.length)}</small>
            </span>
            <MoneyText amount={totals.owed} />
            <Link to={routes.payments}>{tr('viewAllPayments')}</Link>
          </header>
          <ul className="dashboard-debt-list">
            {debtGroups.map(group => (
              <li key={group.student.name} className="dashboard-debt-row">
                <span className="debt-student-avatar" aria-hidden="true">{displayName(group.student.name).charAt(0)}</span>
                <span className="debt-student-identity">
                  <strong>{displayName(group.student.name)}</strong>
                  <small>{tr('debtLessonCount', group.lessons.length)}</small>
                </span>
                <MoneyText amount={group.total} />
                <DebtReminderAction student={group.student} lessons={group.lessons} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {next && (
        <section className="next-lesson-strip">
          <span className="next-lesson-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>
          </span>
          <div><span>{tr('nextLessonTitle')}</span><strong>{displayName(next.student)}</strong></div>
          <bdi>{formatDate(next.date, language)}{next.time ? ` · ${next.time}` : ''}</bdi>
          <MoneyText amount={next.amount} />
        </section>
      )}

      <section className="summary-strip" aria-label={tr('dashboardSummary')}>
        <StatCard label={tr('collectedIncome')} value={<MoneyText amount={yearCollected} />} variant="primary" figure />
        <StatCard label={tr('debt')} value={<MoneyText amount={totals.owed} />} variant="owed" figure />
        <StatCard label={tr('scheduledIncome')} value={<MoneyText amount={totals.scheduled} />} figure />
        <StatCard label={tr('lessonCount')} value={lessons.length.toLocaleString()} />
        <StatCard label={tr('studentCount')} value={students.length.toLocaleString()} />
        <StatCard label={tr('averageLesson')} value={<MoneyText amount={average} />} />
      </section>

      <section className="dashboard-chart-grid" aria-label={tr('dashboardAnalytics')}>
        <article className="dashboard-chart-panel status-chart-panel">
          <ChartHeading title={tr('paymentStatusChart')} hint={tr('paidDebtScheduled')} />
          <div className="status-chart-body">
            <div className="status-donut" style={{
              '--paid-stop': `${collectedLessons.length / Math.max(lessons.length, 1) * 100}%`,
              '--owed-stop': `${(collectedLessons.length + owedLessons.length) / Math.max(lessons.length, 1) * 100}%`
            } as CSSProperties}>
              <strong>{lessons.length}</strong><span>{tr('lessonCount')}</span>
            </div>
            <ul className="chart-legend">
              <li><i className="is-paid" />{tr('paid')} <b>{collectedLessons.length}</b></li>
              <li><i className="is-owed" />{tr('debt')} <b>{owedLessons.length}</b></li>
              <li><i className="is-scheduled" />{tr('scheduled')} <b>{Math.max(0, lessons.length - collectedLessons.length - owedLessons.length)}</b></li>
            </ul>
          </div>
        </article>
        <LineChart title={tr('monthlyIncomeChart')} hint={tr('lastSixMonths')} data={charts.monthlyIncome} />
        <BarChart title={tr('topIncomeStudents')} hint={tr('topFive')} data={charts.studentIncome} money />
        <BarChart title={tr('lessonsByStudent')} hint={tr('topFive')} data={charts.studentLessons} />
      </section>

      {adding && <QuickAddLesson onClose={() => setAdding(false)} />}
      {plannedAdd && (
        <QuickAddLesson
          onClose={() => setPlannedAdd(null)}
          initialStudentName={plannedAdd.student}
          initialDate={plannedAdd.date}
          initialTime={plannedAdd.time}
        />
      )}
    </section>
  );
}

function TodayLesson({ lesson }: { lesson: LessonModel }) {
  const displayName = useDisplayName();
  const { tr } = useTranslation();
  // Cancelled lessons never reach this row (see selectTodayAppointments).
  const state = lesson.status === LessonStatus.Paid ? 'is-paid' : 'is-open';
  return (
    <li className="today-lesson-row">
      <span className="today-avatar" aria-hidden="true">{displayName(lesson.student).charAt(0)}</span>
      <span className="today-student"><strong>{displayName(lesson.student)}</strong>{lesson.time && <bdi className="today-time">{lesson.time}</bdi>}</span>
      <MoneyText amount={lesson.amount} />
      <span className={`today-status ${state}`}>{tr(state === 'is-paid' ? 'paid' : 'unpaid')}</span>
    </li>
  );
}

/**
 * A regular hour that is coming up today with no lesson row yet. The whole
 * row is the way to book it, prefilled, the same as the dashed chip on the
 * calendar; the price shown is the student's, since no amount exists yet.
 */
function TodayPlanned({ item, price, onAdd }: { item: PlannedLesson; price: string | null; onAdd: (item: PlannedLesson) => void }) {
  const displayName = useDisplayName();
  const { tr } = useTranslation();
  return (
    <li className="today-lesson-row is-planned">
      <button type="button" className="today-planned-row" onClick={() => onAdd(item)} title={tr('addLesson')}>
        <span className="today-avatar" aria-hidden="true">{displayName(item.student).charAt(0)}</span>
        <span className="today-student"><strong>{displayName(item.student)}</strong><bdi className="today-time">{item.time}</bdi></span>
        {price ? <MoneyText amount={price} /> : <span />}
        <span className="today-status is-scheduled">{tr('scheduled')}</span>
      </button>
    </li>
  );
}

function StatCard({ label, value, variant = '', figure = false }: { label: string; value: React.ReactNode; variant?: string; figure?: boolean }) {
  return <article className={`dashboard-stat ${variant ? `is-${variant}` : ''}`}><span>{label}</span><strong className={figure ? 'figure-value' : undefined}>{value}</strong></article>;
}

function ChartHeading({ title, hint }: { title: string; hint: string }) {
  return <header className="chart-heading"><h2>{title}</h2><p>{hint}</p></header>;
}

function BarChart({ title, hint, data, money = false }: { title: string; hint: string; data: BarDatum[]; money?: boolean }) {
  // Both charts rank students, and a datum carries the identity, not the spelling.
  const displayName = useDisplayName();
  const max = Math.max(...data.map(item => item.value), 1);
  return (
    <article className="dashboard-chart-panel">
      <ChartHeading title={title} hint={hint} />
      <ul className="dashboard-bars">
        {data.map(item => (
          <li key={item.label}>
            <span title={displayName(item.label)}>{displayName(item.label)}</span>
            <i><b style={{ width: `${Math.max(item.value ? 5 : 0, item.value / max * 100)}%` }} /></i>
            <strong>{money ? <MoneyText amount={item.value} /> : item.value.toLocaleString()}</strong>
          </li>
        ))}
      </ul>
    </article>
  );
}

/**
 * Reserved as a share of the plot's height: room above the line for a value
 * label over every point, and a sliver below so the lowest point's marker
 * never sits flush on the panel's edge.
 */
const LINE_CHART_TOP_PAD = 26;
const LINE_CHART_BOTTOM_PAD = 6;

/**
 * Reserved on each side, horizontally. Every point's value label and the
 * first/last month labels are centred *on* their point, so without this the
 * two end labels would have their outer half hanging past the plot's own
 * edge instead of inside the card.
 */
const LINE_CHART_SIDE_PAD = 11;

/**
 * A trend reads left to right whatever the interface language is, the same
 * way a plotted number stays left-to-right beside Hebrew text: `dir="ltr"` is
 * set on the chart itself rather than left to inherit the page's direction,
 * so the months never run newest-to-oldest under RTL.
 */
function LineChart({ title, hint, data }: { title: string; hint: string; data: BarDatum[] }) {
  const max = Math.max(...data.map(item => item.value), 1);
  const plotTop = LINE_CHART_TOP_PAD;
  const plotBottom = 100 - LINE_CHART_BOTTOM_PAD;
  const plotWidth = 100 - LINE_CHART_SIDE_PAD * 2;
  const xFor = (index: number) => data.length > 1
    ? LINE_CHART_SIDE_PAD + (index / (data.length - 1)) * plotWidth
    : 50;
  const points = data.map((item, index) => ({
    ...item,
    x: xFor(index),
    y: plotBottom - (item.value / max) * (plotBottom - plotTop)
  }));
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');

  return (
    <article className="dashboard-chart-panel">
      <ChartHeading title={title} hint={hint} />
      <div className="income-chart" dir="ltr">
        <div className="income-chart-plot">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <path d={path} className="income-chart-line" vectorEffect="non-scaling-stroke" />
          </svg>
          {points.map(point => (
            <div key={point.label} className="income-chart-point" style={{ left: `${point.x}%`, top: `${point.y}%` }}>
              <span className="income-chart-value"><MoneyText amount={point.value} /></span>
              <span className="income-chart-dot" aria-hidden="true" />
            </div>
          ))}
        </div>
        <div className="income-chart-axis">
          {points.map(point => (
            <span key={point.label} style={{ left: `${point.x}%` }}>{point.label}</span>
          ))}
        </div>
      </div>
    </article>
  );
}

function buildCharts(lessons: LessonModel[], language: Language) {
  const now = new Date();
  const months: BarDatum[] = [];
  for (let index = 5; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString(language === 'he' ? 'he-IL' : 'en-GB', { month: 'short' });
    const value = lessons.filter(lesson => lesson.date.startsWith(key) && isCollected(lesson)).reduce((sum, lesson) => sum + amountValue(lesson.amount), 0);
    months.push({ label, value });
  }
  const income = new Map<string, number>();
  const counts = new Map<string, number>();
  for (const lesson of lessons) {
    counts.set(lesson.student, (counts.get(lesson.student) ?? 0) + 1);
    if (isCollected(lesson)) income.set(lesson.student, (income.get(lesson.student) ?? 0) + amountValue(lesson.amount));
  }
  const top = (map: Map<string, number>) => [...map].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([label, value]) => ({ label, value }));
  return { monthlyIncome: months, studentIncome: top(income), studentLessons: top(counts) };
}
